import { Box, Spinner } from "@chakra-ui/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import NuSingleSelect from "./NuSingleSelect";
import NuSelectItem from "./NuSelectItem";
import { ChakraFAIcon } from "@/components/ui/Icon";

export default class NuMultiSelect extends NuSingleSelect {
	constructor(props) {
		super(props);
		this.state = {
			selectedItems: [], //this.findSelectedItems(this.props.selectedItems),
			filteredItems: [],
			filter: "",
			openSelection: false,
			cbLoading: false,
		};

		// Override the handleChangeBounced method to respect minChars constraint
		this.handleChangeBounced = (e) => {
			this.props.onChange(e);
			const newValue = e.target.value;
			this.setState({ filter: newValue });

			// Always open dropdown, but only populate with results if minChars met
			if (newValue.length >= (this.props.minChars || 0)) {
				this.setState({ openSelection: true });
				// Call the search method directly instead of parent's handleChange
				this.prepareFilteredItems(newValue).then((filteredItems) => {
					this.setState({
						filteredItems: this.props.showAllSelectItems ? this.props.allItems : filteredItems,
						openSelection: filteredItems.length !== 0,
					});
				});
			} else {
				// Keep dropdown open but clear results, show placeholder
				this.setState({
					openSelection: true,
					filteredItems: [],
				});
			}
		};
	}

	findSelectedItems = (selectedValues) => {
		const selectedItems = [];
		for (const value of selectedValues) {
			for (const item of this.props.allItems) {
				if (item["value"] === value) {
					selectedItems.push(item);
					break;
				}
			}
			/* We have failed to match with an existing item */
			if (selectedItems.length === 0 || selectedItems[selectedItems.length - 1]["value"] !== value) {
				selectedItems.push({
					text: value,
					value: value,
				});
			}
		}
		return selectedItems;
	};

	componentDidMount() {
		document.addEventListener("mousedown", this.handleClickOutside, { capture: true });
		window.addEventListener("blur", this.handleWindowBlur);
		this.setState({
			selectedItems: this.findSelectedItems(this.props.selectedItems),
		});
	}

	componentWillUnmount() {
		window.removeEventListener("blur", this.handleWindowBlur);
		document.removeEventListener("mousedown", this.handleClickOutside, { capture: true });
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		if (this.state.cbLoading) {
			return;
		}
		this.setState({
			selectedItems: this.findSelectedItems(nextProps.selectedItems),
		});
	}

	handleAdd = async (item) => {
		this.setState({ cbLoading: true });

		this.inputRef.blur();
		this.hiddenFocusDivRef.focus();

		this.setState({
			openSelection: false,
			filter: "",
			filteredItems: [],
		});
		await this.props.onAdd(item["value"]);
		this.setState({ cbLoading: false });
	};

	// Overrides handleSelect in NuSingleSelect, this function is used when an item is selected via keyboard navigation
	handleSelect = async (item) => {
		this.handleAdd(item);
	};

	handleRemove = async (item) => {
		if (this.props.disabled || this.props.disableRemove) {
			return;
		}

		this.setState({ cbLoading: true });
		try {
			const result = await this.props.onRemove(item["value"]);
			if (result === false) {
				return;
			}

			this.setState((prevState) => {
				const newSelectedItems = [...prevState.selectedItems];
				const index = newSelectedItems.findIndex((i) => i.value === item.value || i.text === item.text);
				if (index >= 0) {
					newSelectedItems.splice(index, 1);
				}
				return { selectedItems: newSelectedItems };
			});
		} finally {
			this.setState({ cbLoading: false });
		}
	};

	prepareFilteredItems = async (filter) => {
		// reset the keyboard focussed item tracking
		this.currentOptionIndex = -1;
		if (this.currentOption) {
			this.currentOption.classList.remove("NuSelect_searchSelectItem--keyboard-focused");
		}
		this.currentOption = null;

		//If there is a callback for allItems, call that now
		let allItems = this.props.allItems;
		if (this.props.allItemsCb) {
			this.setState({ cbLoading: true });
			allItems = await this.props.allItemsCb(filter);
			this.setState({ cbLoading: false });
		}

		const filteredItems = [];
		for (const item of allItems) {
			if (item.dynamic === true) {
				var transformedValue = item.transformFilter(filter);
				if (item.shouldShow(transformedValue)) {
					filteredItems.push({
						text: item.renderText(transformedValue),
						value: transformedValue,
						ok: item.isOk(transformedValue),
					});
				}
			} else {
				// Use minChars constraint instead of hardcoded length checks
				const minChars = this.props.minChars || 0;
				if (filter.length >= minChars) {
					if (filter.length <= 2 && item["text"].toLowerCase().startsWith(filter.toLowerCase())) {
						filteredItems.push(item);
					} else if (filter.length > 2 && item["text"].toLowerCase().includes(filter.toLowerCase())) {
						filteredItems.push(item);
					}
				} else if (filter.length === 0) {
					filteredItems.push(item);
				}
			}
		}

		for (const selectedItem of this.state.selectedItems) {
			for (let i = 0; i < filteredItems.length; i++) {
				if (selectedItem["value"] === filteredItems[i]["value"]) {
					filteredItems.splice(i, 1);
					break;
				}
			}
		}

		return filteredItems.slice(0, 100);
	};

	renderItems = () => {
		const preparedItems = [];

		// Show placeholder when input is less than minChars
		if (this.state.filter.length > 0 && this.state.filter.length < (this.props.minChars || 0)) {
			preparedItems.push(
				<Box
					key="minchars_placeholder"
					className="NuSelect_searchSelectItem NuSelect_searchSelectItem--placeholder"
					color="neutrals.300"
					px={6}
					py={2}
				>
					{`Enter at least ${this.props.minChars} characters to search`}
				</Box>
			);
		} else {
			// Show actual search results
			for (const item of this.state.filteredItems) {
				preparedItems.push(
					<div key={item["text"]} className="NuSelect_searchSelectItem" onClick={async () => this.handleAdd(item)}>
						{item["text"]}
					</div>
				);
			}

			if (this.state.filteredItems.length > 99) {
				preparedItems.push(
					<div key="truncated_items_info" className="NuSelect_searchSelectItemTruncateInfo">
						Too many values to show them all. Please use search to narrow down results.
					</div>
				);
			}
		}

		return preparedItems;
	};

	renderSelectedItems = () => {
		const renderItems = [];
		for (const item of this.state.selectedItems) {
			if (item !== undefined && item !== null) {
				renderItems.push(
					<NuSelectItem
						key={item["value"]}
						item={item}
						handleChange={this.handleRemove}
						showClose={!this.props.disabled && !this.props.disableRemove}
					/>
				);
			}
		}
		return renderItems;
	};

	handleFocus = async () => {
		// Always open dropdown, but content depends on minChars
		this.setState({
			openSelection: true,
			filteredItems: this.state.filter.length >= (this.props.minChars || 0) ? await this.prepareFilteredItems(this.state.filter) : [],
		});
	};

	handleClick = async () => {
		this.inputRef.focus();
		// Always open dropdown, but content depends on minChars
		this.setState({
			openSelection: true,
			filteredItems: this.state.filter.length >= (this.props.minChars || 0) ? await this.prepareFilteredItems(this.state.filter) : [],
		});
	};

	render() {
		return (
			<div>
				{this.props.title && <div className="NuSelect_selectedTitle">{this.props.title}</div>}
				{!this.props.disabled && (
					<div
						className={clsx(this.state.openSelection && "NuSelect_searchOpen")}
						onFocus={this.setFocussedClass}
						onBlur={this.removeFocussedClass}
					>
						<div
							ref={this.setSearchContainerRef}
							className={clsx(
								"NuSelect_searchField",
								this.props.invalid && "invalid",
								this.props.loading && "NuSelect_searchField--disabled"
							)}
						>
							<ChakraFAIcon
								ref={(node) => (this.searchIconRef = node)}
								icon="fa-search"
								variant="solid"
								className="NuSelect_searchFieldSearchIcon"
							/>
							<input
								className={clsx("NuSelect_searchFieldInput", !this.state.openSelection && !this.props.disabled && "clickable")}
								ref={this.setInputRef}
								type="text"
								value={this.state.filter}
								onChange={this.handleChangeBounced}
								placeholder={this.props.loading ? "Loading..." : this.props.searchPlaceholder}
								onFocus={this.handleFocus}
								onKeyDown={this.handleInputKeyDown}
								// keyUp used by our Modal class uses keyUp for it's enter handler, we need to stop propagation to that handler so we need to use keyUp here
								onKeyUp={this.handleInputEnterKeyUp}
								data-cy={this.props.dataCyInput}
								data-testid={this.props.dataTestId}
								disabled={this.props.loading || this.props.disabled}
							/>
							<Box onClick={this.handleClick}>
								{this.state.cbLoading ? (
									<Spinner ref={(node) => (this.arrowIconRef = node)} />
								) : (
									<ChakraFAIcon
										ref={(node) => (this.arrowIconRef = node)}
										icon="fa-angle-down"
										variant="solid"
										className="NuSelect_searchFieldArrowIcon"
									/>
								)}
							</Box>
						</div>
						{this.renderSelection()}
					</div>
				)}
				{!this.props.loading && (
					<div className="NuSelect_selected" data-cy={this.props.dataCyItemsContainer} data-testid={this.props.dataCyItemsContainer}>
						{this.renderSelectedItems()}
					</div>
				)}
				{/* focus is set here after a selection is made, allows fluent tab navigation back to select, or forward to next tabbable element */}
				<div tabIndex={-1} ref={(node) => (this.hiddenFocusDivRef = node)} className="NuSelect_hiddenFocusDiv" />
			</div>
		);
	}
}

NuMultiSelect.propTypes = {
	dataCyInput: PropTypes.string,
	dataCyItemsContainer: PropTypes.string,
	dataTestId: PropTypes.string,
	searchPlaceholder: PropTypes.string,
	selectedItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
	allItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
	minChars: PropTypes.number,
	onChange: PropTypes.func,
	disableRemove: PropTypes.bool,
};

NuMultiSelect.defaultProps = {
	allItems: [],
	loading: false,
	minChars: 0,
	onChange: () => {},
	disableRemove: false,
};

NuSingleSelect.defaultProps = {
	allItems: [],
	loading: false,
	minChars: 0,
	onChange: () => {},
};
